import { addJstDays, differenceInJstDays, formatJstDate } from "../time-utils";

/**
 * 分析（レポート）の集計ロジック。
 *
 * ストアダッシュボードと代理店ダッシュボードで同じ数値を出す必要があるため、
 * 計算式はこのモジュールを唯一の正本とする（#1273）。
 * SNS インサイトは Firestore の `SNSInsightsType` を渡す想定だが、
 * `@unit/firestore` へ依存すると循環参照になるため集計に必要な項目のみを構造的に要求する。
 *
 * 日付はすべて JST の暦日（yyyy-MM-dd）で扱う。API / Firestore の日別キーが JST 基準のため、
 * 端末のタイムゾーンで日付を組み立てると JST 以外の環境でキーがずれる。
 */
export type SnsInsightMetrics = {
  snsPostId: string;
  /** yyyy-MM-dd */
  date: string;
  /**
   * 同じ投稿を複数のチケットが共有することがあるため、重複排除のキーに使う（#1328）。
   * 旧形式のドキュメントにも入っている。
   */
  influencerTicketId?: string;
  /** Firestore への書き込み時刻。同一投稿・同一日の重複を解決するのに使う（#1328） */
  updatedAt?: number;
  likeCount: number;
  commentCount: number;
  impression: number;
  reach: number;
  keepCount: number;
  engagement: number;
  createdAt: number;
};

export type SummaryChartDatum = {
  date: string;
  data1: number;
  data2: number;
  dateRange: { startDate: string; endDate: string };
};

/**
 * 前期比の増減率（%）。小数第 2 位まで。
 *
 * 比較元が 0 のときは 0 を返す。0 からの増加は率として定義できないため、
 * 「増減なし」ではなく「率を出せない」ことを呼び出し側で文言化する前提の値。
 */
export const calculatePercentageIncrease = (oldValue: number, newValue: number): number => {
  if (!oldValue) {
    return 0;
  }
  const percentageIncrease = ((newValue - oldValue) / oldValue) * 100;
  return Number(percentageIncrease.toFixed(2));
};

export const calculateEngagementRate = (engagementCount: number, reachCount: number): number => {
  if (!engagementCount || !reachCount) {
    return 0;
  }

  const engagementRate = (engagementCount / reachCount) * 100;
  return Number(engagementRate.toFixed(2));
};

export const convertUnixTimestampToYYYYMMDD = (timestamp: number, separator?: string): string => {
  const jstDate = formatJstDate(new Date(timestamp * 1000));
  return separator ? jstDate.split("-").join(separator) : jstDate;
};

/** 集計の終端となる JST の暦日。当日はデータの取り込みが未完了になりうるため前日とする */
export const getReportEndDay = (now: Date = new Date()): string => addJstDays(formatJstDate(now), -1);

/**
 * 取組み開始からの「N日目」。開始日当日を 1日目 とする（#1518）。
 *
 * 実時間の 24 時間差で数えると、日付が変わる 0:00 ではなくストア作成時刻に繰り上がり、
 * 併記している開始日（JST の暦日）と 1〜2 日ずれる。このモジュールの他の関数と同じく
 * JST の暦日に落としてから差を取る。
 */
export const nthDaySinceTimestamp = (timestamp: number, now: Date = new Date()): number =>
  differenceInJstDays(formatJstDate(new Date(timestamp * 1000)), formatJstDate(now)) + 1;

/** 日次データを累計に変換したうえで、表示件数が増えすぎないよう間引く */
export const calculateSummaryChartData = ({
  data1,
  data2,
  storeCreatedDate,
}: {
  data1?: { [key: string]: number };
  data2?: { [key: string]: number };
  storeCreatedDate: Date;
}): SummaryChartDatum[] => {
  const data: { date: string; data1: number; data2: number }[] = [];
  eachReportDay(storeCreatedDate, (date) => {
    const count1 = (data1 ? data1[date] || 0 : 0) + (data.at(-1)?.data1 || 0);
    const count2 = (data2 ? data2[date] || 0 : 0) + (data.at(-1)?.data2 || 0);
    data.push({ date, data1: count1, data2: count2 });
  });
  return calculateSpacedData(data);
};

/**
 * 起点の JST 暦日から集計終端（前日）まで 1 日ずつ走査する。
 * 端末のタイムゾーンに関わらず、API / Firestore と同じ JST の暦日キーを組み立てる。
 */
const eachReportDay = (startDate: Date, callback: (jstDate: string) => void): void => {
  const endDay = getReportEndDay();
  let day = formatJstDate(startDate);
  while (day <= endDay) {
    callback(day);
    day = addJstDays(day, 1);
  }
};

/** 表示点数が増えすぎないよう、点の数に応じて 1/2・1/4... と間引く */
const calculateSpacedData = (data: { date: string; data1: number; data2: number }[]): SummaryChartDatum[] => {
  let skip = 1;
  let limit = 60;
  while (limit < data.length) {
    skip *= 2;
    limit *= 2;
  }
  const spacedData: SummaryChartDatum[] = [];
  for (let i = 0; i < data.length; ) {
    const d = data[i];
    let endDateRange = d.date;
    let maxData1 = 0;
    let maxData2 = 0;
    for (let j = 0; j < skip && i + j < data.length; j++) {
      const skipData = data[i + j];
      maxData1 = Math.max(maxData1, skipData.data1);
      maxData2 = Math.max(maxData2, skipData.data2);
      endDateRange = skipData.date;
    }
    spacedData.push({
      date: d.date,
      data1: maxData1,
      data2: maxData2,
      dateRange: { startDate: d.date, endDate: endDateRange },
    });
    i += skip;
  }

  return spacedData;
};

/** 累計に変換せず、欠測日は直前の値で埋めたうえで間引く（インサイト系の累積値向け） */
export const calculateSummaryChartDataWithoutSum = ({
  data1,
  data2,
  storeCreatedDate,
}: {
  data1?: { [key: string]: number };
  data2?: { [key: string]: number };
  storeCreatedDate: Date;
}): SummaryChartDatum[] => {
  const data = calculateDataByDate({ data1, data2, storeCreatedDate });
  return calculateSpacedData(data);
};

/** 間引かずに 1 日 1 点で並べる（最新値の取得などに使う） */
export const calculateDataByDate = ({
  data1,
  data2,
  storeCreatedDate,
}: {
  data1?: { [key: string]: number };
  data2?: { [key: string]: number };
  storeCreatedDate: Date;
}): { date: string; data1: number; data2: number }[] => {
  const data: { date: string; data1: number; data2: number }[] = [];
  eachReportDay(storeCreatedDate, (date) => {
    const count1 = data1 ? data1[date] || data.at(-1)?.data1 || 0 : 0;
    const count2 = data2 ? data2[date] || data.at(-1)?.data2 || 0 : 0;
    data.push({ date, data1: count1, data2: count2 });
  });
  return data;
};

export const groupInsightsDataByDate = (snsInsightsData: SnsInsightMetrics[]) => {
  return (
    snsInsightsData.reduce((prev, curr) => {
      if (curr.date in prev) {
        return {
          ...prev,
          [curr.date]: {
            reach: prev[curr.date].reach + (curr.reach ?? 0),
            impression: prev[curr.date].impression + (curr.impression ?? 0),
            engagement: prev[curr.date].engagement + (curr.engagement ?? 0),
            like: prev[curr.date].like + (curr.likeCount ?? 0),
            comment: prev[curr.date].comment + (curr.commentCount ?? 0),
            keep: prev[curr.date].keep + (curr.keepCount ?? 0),
          },
        };
      } else {
        return {
          ...prev,
          [curr.date]: {
            reach: curr.reach ?? 0,
            impression: curr.impression ?? 0,
            engagement: curr.engagement ?? 0,
            like: curr.likeCount ?? 0,
            comment: curr.commentCount ?? 0,
            keep: curr.keepCount ?? 0,
          },
        };
      }
    }, {} as { [key: string]: { reach: number; impression: number; engagement: number; like: number; comment: number; keep: number } }) ??
    {}
  );
};

export const getInsightsDataCounts = (snsInsightsData: SnsInsightMetrics[]) => {
  const insightsDataBySnsPostId =
    snsInsightsData.reduce((prev, curr) => {
      if (curr.snsPostId in prev) {
        if (prev[curr.snsPostId].createdAt > curr.createdAt) return { ...prev };
        else return { ...prev, [curr.snsPostId]: curr };
      } else {
        return { ...prev, [curr.snsPostId]: curr };
      }
    }, {} as { [key: string]: SnsInsightMetrics }) ?? {};
  const initialValue = {
    reachCount: 0,
    impressionCount: 0,
    engagementCount: 0,
    likeCount: 0,
    commentCount: 0,
    engagementRate: 0,
    keepCount: 0,
  };
  const insightsDataCounts =
    Object.values(insightsDataBySnsPostId).reduce((prev, curr) => {
      return {
        reachCount: prev.reachCount + (curr.reach ?? 0),
        impressionCount: prev.impressionCount + (curr.impression ?? 0),
        engagementCount: prev.engagementCount + (curr.engagement ?? 0),
        likeCount: prev.likeCount + (curr.likeCount ?? 0),
        commentCount: prev.commentCount + (curr.commentCount ?? 0),
        engagementRate: calculateEngagementRate(
          prev.engagementCount + (curr.engagement ?? 0),
          prev.reachCount + (curr.reach ?? 0),
        ),
        keepCount: prev.keepCount + (curr.keepCount ?? 0),
      };
    }, initialValue) ?? initialValue;
  return insightsDataCounts;
};

/** 来店者数はリーチ数の 2%（当社実績値） */
export const calculateVisitorCount = (reachCount: number): number => {
  const visitorCount = Math.floor((reachCount / 100) * 2);
  return visitorCount;
};

/** 売上は「来店者数 × 客単価」 */
export const calculateEarnings = (visitorCount: number, averageCustomerSpend: number): number => {
  const earnings = visitorCount * averageCustomerSpend;
  return earnings;
};

/** 累積指標の合計。`updatedAt` が同値のときの決着に使う（累積なので新しい方が必ず大きいか等しい）。 */
const cumulativeTotalOf = (insight: SnsInsightMetrics): number =>
  (insight.reach ?? 0) +
  (insight.impression ?? 0) +
  (insight.likeCount ?? 0) +
  (insight.commentCount ?? 0) +
  (insight.keepCount ?? 0);

/** 後から書かれた方か。同値のときも取得順に依存しないよう決着させる。 */
const isNewerInsight = (candidate: SnsInsightMetrics, current: SnsInsightMetrics): boolean => {
  const candidateUpdatedAt = candidate.updatedAt ?? 0;
  const currentUpdatedAt = current.updatedAt ?? 0;
  if (candidateUpdatedAt !== currentUpdatedAt) return candidateUpdatedAt > currentUpdatedAt;
  // updatedAt は秒精度で、同一秒に書かれると差がつかない。Firestore の取得順は保証されないため、
  // ここで順序に頼ると表示値が実行ごとに変わる。累積指標は減らないので、大きい方を後の書き込みとみなす
  return cumulativeTotalOf(candidate) > cumulativeTotalOf(current);
};

/**
 * 同一の投稿・同一日のインサイトが複数あるとき、書き込みが新しい方だけを残す（#1328）。
 *
 * 日次インサイトのドキュメント ID は当初 `influencerTicketId + snsAccountType + date` で
 * 投稿を区別していなかった。unit-api#987 で `snsPostId` を含む形へ変えるため、
 * **新旧のドキュメントが併存する**。読み手側で落とさないと同じ投稿が二重に計上される。
 *
 * 過去日の旧ドキュメントは対応する新ドキュメントが存在しないため、そのまま残る（履歴が消えない）。
 */
export const dedupeInsightsByPostAndDate = <T extends SnsInsightMetrics>(insightsData: T[]): T[] => {
  const deduped: T[] = [];
  const indexByKey = new Map<string, number>();
  for (const insight of insightsData) {
    // キーが欠けているレコードは重複と判定しない。空文字などで代用すると、
    // 別チケットの正当なインサイトを同一視して捨てる。
    // 型で必須にする案もあるが、`fillMissingInsightsData` は本来「欠測日の補完」が役目で、
    // 重複排除のキーまで要求するのは呼び出し側への制約が強すぎる（既存の呼び出しが通らなくなる）
    if (insight.influencerTicketId === undefined || insight.updatedAt === undefined) {
      deduped.push(insight);
      continue;
    }
    // 同じ投稿を複数のチケットが共有することがあるため、influencerTicketId を含めないと
    // 別チケットの正当なインサイトを重複と誤判定して捨てる
    const key = `${insight.influencerTicketId}\u0000${insight.snsPostId}\u0000${insight.date}`;
    const prevIndex = indexByKey.get(key);
    if (prevIndex === undefined) {
      indexByKey.set(key, deduped.length);
      deduped.push(insight);
      continue;
    }
    // createdAt は投稿の作成日時なので同日の重複を解決できない。updatedAt（書き込み時刻）で新しい方を採る。
    // 位置は最初に現れたところを保つ（後段の欠測補完が「同日の最初の1件」を基準にするため）
    if (isNewerInsight(insight, deduped[prevIndex])) {
      deduped[prevIndex] = insight;
    }
  }
  return deduped;
};

/**
 * インサイトの欠測日を直前の値で補完する。
 * 取得できていない日があっても累積値のグラフが谷にならないようにするための前処理。
 *
 * 補完の前に、新旧ドキュメントの重複を落とす（`dedupeInsightsByPostAndDate`）。
 */
export const fillMissingInsightsData = <T extends SnsInsightMetrics>(insightsData: T[]): T[] => {
  const filledInsightsData: T[] = [];
  const map = new Map<string, T[]>();
  for (const insight of dedupeInsightsByPostAndDate(insightsData)) {
    const { snsPostId } = insight;
    if (map.has(snsPostId)) {
      map.get(snsPostId)?.push(insight);
    } else {
      map.set(snsPostId, [insight]);
    }
  }
  const endDay = formatJstDate(new Date());
  for (const insights of map.values()) {
    // date は JST の暦日（yyyy-MM-dd）なので、文字列比較がそのまま日付順になる
    insights.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    let prevInsight = insights[0];
    prevInsight.engagement =
      (prevInsight.likeCount ?? 0) + (prevInsight.commentCount ?? 0) + (prevInsight.keepCount ?? 0);
    const missingInsights: T[] = [];
    for (let date = insights[0].date; date <= endDay; date = addJstDays(date, 1)) {
      const insight = insights.find((insight) => insight.date === date);
      if (insight) {
        if (insight.commentCount < prevInsight.commentCount) {
          insight.commentCount = prevInsight.commentCount;
        }
        if (insight.likeCount < prevInsight.likeCount) {
          insight.likeCount = prevInsight.likeCount;
        }
        if (insight.impression < prevInsight.impression) {
          insight.impression = prevInsight.impression;
        }
        if (insight.reach < prevInsight.reach) {
          insight.reach = prevInsight.reach;
        }
        if (insight.keepCount < prevInsight.keepCount) {
          insight.keepCount = prevInsight.keepCount;
        } else if (!insight.keepCount) {
          insight.keepCount = 0;
        }
        insight.engagement = (insight.likeCount ?? 0) + (insight.commentCount ?? 0) + (insight.keepCount ?? 0);
        prevInsight = insight;
      } else {
        missingInsights.push({
          ...prevInsight,
          date,
        });
      }
    }
    insights.push(...missingInsights);
  }
  for (const insights of map.values()) {
    filledInsightsData.push(...insights);
  }
  return filledInsightsData;
};
